import React, { useState, useEffect } from 'react'
import { PublicBrandPage } from './PublicBrandPage'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Globe, ArrowLeft, Home, Search } from 'lucide-react'

interface Brand {
  id: string
  name: string
  description?: string
  website_url?: string
  is_public?: boolean
  slug?: string
  metadata?: {
    industry?: string
  }
}

interface PublicBrandPageRouterProps {
  slug: string
  onBack?: () => void
}

export function PublicBrandPageRouter({ slug, onBack }: PublicBrandPageRouterProps) {
  const [brand, setBrand] = useState<Brand | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    loadBrandBySlug()
  }, [slug])

  const loadBrandBySlug = async () => {
    setIsLoading(true)
    setNotFound(false)

    try {
      // In a real app, this would be an API call
      // For now, we'll simulate finding a brand
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulate loading

      // Mock brand data - in real app this would come from API
      const mockBrand: Brand = {
        id: 'brand-123',
        name: slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' '),
        description: 'A forward-thinking technology company focused on innovative solutions',
        website_url: `https://${slug}.com`,
        is_public: true,
        slug: slug,
        metadata: {
          industry: 'Technology'
        }
      }

      // Check if brand exists and is public
      if (slug === 'demo-brand' || slug === 'techflow' || slug === 'brandlab') {
        setBrand(mockBrand)
      } else {
        setNotFound(true)
      }
    } catch (error) {
      console.error('Error loading brand:', error)
      setNotFound(true)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Globe className="w-6 h-6 text-white animate-pulse" />
          </div>
          <p className="text-slate-600">Loading brand page...</p>
        </div>
      </div>
    )
  }

  if (notFound || !brand) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <Card>
              <CardHeader>
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-slate-400" />
                </div>
                <CardTitle>Brand Page Not Found</CardTitle>
                <CardDescription>
                  The brand page you're looking for doesn't exist or has been moved.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-600">
                  The slug "{slug}" doesn't match any published brand pages.
                </p>
                <div className="flex flex-col gap-3">
                  {onBack ? (
                    <Button onClick={onBack}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Go Back
                    </Button>
                  ) : (
                    <Button onClick={() => window.location.href = '/'}>
                      <Home className="w-4 h-4 mr-2" />
                      Go to Homepage
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => window.location.href = '/demo-brand'}>
                    <Globe className="w-4 h-4 mr-2" />
                    View Demo Brand
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return <PublicBrandPage brand={brand} onBack={onBack} />
}